/**
 * Input factory - maps field types to input components
 */

import React from "react";
import type { SimpleFieldConfig } from "../../../types/index";
import type { InputFactoryConfig, InputFactoryResult } from "../types";

const getReferencedTable = (fieldConfig: SimpleFieldConfig): string | null =>
  fieldConfig.isForeignKey ? fieldConfig.foreignKey?.referencedTable ?? null : null;

const getReferencedColumn = (fieldConfig: SimpleFieldConfig): string | null =>
  fieldConfig.isForeignKey ? fieldConfig.foreignKey?.referencedColumn ?? null : null;

const FOREIGN_KEY_INPUT_TYPE_MAP: Record<string, string> = {
  "contacts.id": "contact",
  "companies.id": "company",
  "tickets.id": "ticket",
  "app_users.id": "user",
};

const getInputTypeFromForeignKey = (fieldConfig: SimpleFieldConfig): string | null => {
  const referencedTable = getReferencedTable(fieldConfig);
  const referencedColumn = getReferencedColumn(fieldConfig);
  if (!referencedTable || !referencedColumn) return null;

  return FOREIGN_KEY_INPUT_TYPE_MAP[`${referencedTable}.${referencedColumn}`] ?? null;
};

const isCompanyIdField = (fieldId: string): boolean => {
  const normalized = fieldId.toLowerCase();
  if (normalized === "company_id" || normalized === "company" || normalized === "companyid") {
    return true;
  }
  if (normalized.includes("company") && !normalized.includes("name")) {
    return true;
  }
  return false;
};

/**
 * Determine if a field should use auto-save
 */
export function shouldUseAutoSave(fieldId: string, fieldConfig: SimpleFieldConfig): boolean {
  const foreignKeyInputType = getInputTypeFromForeignKey(fieldConfig);
  if (foreignKeyInputType) {
    return true;
  }

  // Multi-user fields always auto-save
  if (fieldConfig.type === "multi-user") return true;

  // User/assignment fields auto-save
  if (
    fieldId === "assigned_to" ||
    fieldId === "user_id" ||
    fieldId?.includes("user") ||
    fieldId?.includes("assign")
  ) {
    return true;
  }

  // Ticket fields auto-save
  if (fieldId === "ticket_id" || fieldId?.includes("ticket")) return true;

  // Company fields auto-save
  if (isCompanyIdField(fieldId)) return true;

  // Contact/customer fields auto-save
  if (
    fieldId === "contact_id" ||
    fieldId?.includes("contact") ||
    fieldId === "customer_id" ||
    fieldId?.includes("customer")
  ) {
    return true;
  }

  // Email aliases auto-save
  if (fieldId === "email_aliases") return true;
  if (fieldId === "phone_aliases") return true;

  // Select fields auto-save
  if (fieldConfig.type === "select" && fieldConfig.options) return true;

  // Boolean fields auto-save
  if (fieldConfig.type === "boolean") return true;

  return false;
}

/**
 * Determine input type for a field
 */
export function getInputType(fieldId: string, fieldConfig: SimpleFieldConfig): string {
  // Drive folder id is chosen via the Drive browser modal, never typed.
  if (fieldId === "google_drive_folder_id") return "drive-folder";

  const foreignKeyInputType = getInputTypeFromForeignKey(fieldConfig);
  if (foreignKeyInputType) return foreignKeyInputType;

  if (fieldConfig.type === "multi-user") return "multi-user";
  if (fieldId === "assigned_to" || fieldId === "user_id" || fieldId?.includes("user") || fieldId?.includes("assign")) {
    return "user";
  }
  if (fieldId === "ticket_id" || fieldId?.includes("ticket")) return "ticket";
  if (isCompanyIdField(fieldId)) return "company";
  if (fieldId === "contact_id" || fieldId?.includes("contact") || fieldId === "customer_id" || fieldId?.includes("customer")) {
    return "contact";
  }
  if (fieldId === "email_aliases") return "email-aliases";
  if (fieldId === "phone_aliases") return "phone-aliases";
  if (fieldConfig.type === "select" && fieldConfig.options) return "select";
  if (fieldConfig.type === "date" || fieldConfig.type === "due-date") return "date";
  if (fieldConfig.type === "datetime") return "datetime";
  if (fieldId === "close_probability" && fieldConfig.type === "number" && fieldConfig.validation?.min === 0 && fieldConfig.validation?.max === 100) {
    return "probability";
  }
  if (fieldConfig.type === "number") return "number";
  if (fieldConfig.type === "boolean") return "boolean";
  if (fieldConfig.type === "textarea" || fieldId === "body" || fieldId?.includes("description") || fieldId?.includes("notes") || fieldId?.includes("content")) {
    return "textarea";
  }
  return "text";
}

/**
 * Check if field is a special input type
 */
export function isSpecialInputType(inputType: string): boolean {
  const specialTypes = [
    "multi-user",
    "user",
    "ticket",
    "company",
    "contact",
    "email-aliases",
    "select",
    "probability",
    "boolean",
    "drive-folder",
  ];
  return specialTypes.includes(inputType);
}

/**
 * Get input component name
 */
export function getInputComponentName(inputType: string): string {
  const componentMap: Record<string, string> = {
    "multi-user": "MultiUserInput",
    user: "UserInput",
    ticket: "TicketInput",
    company: "CompanyInput",
    contact: "ContactInput",
    "email-aliases": "EmailAliasesInput",
    select: "SelectInput",
    date: "DateInput",
    datetime: "DateTimeInput",
    probability: "ProbabilitySliderEdit",
    number: "NumberInput",
    boolean: "BooleanInput",
    "drive-folder": "DriveFolderInput",
    textarea: "TextAreaInput",
    text: "TextInput",
  };
  return componentMap[inputType] || "TextInput";
}

/**
 * Validate input value for a field
 */
export function validateInputValue(value: string, fieldConfig: SimpleFieldConfig): { valid: boolean; error?: string } {
  if (fieldConfig.validation?.required && !value) {
    return { valid: false, error: "This field is required" };
  }

  if (fieldConfig.type === "number") {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return { valid: false, error: "Must be a number" };
    }
    if (fieldConfig.validation?.min !== undefined && num < fieldConfig.validation.min) {
      return { valid: false, error: `Must be at least ${fieldConfig.validation.min}` };
    }
    if (fieldConfig.validation?.max !== undefined && num > fieldConfig.validation.max) {
      return { valid: false, error: `Must be at most ${fieldConfig.validation.max}` };
    }
  }

  if (fieldConfig.type === "date") {
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return { valid: false, error: "Invalid date format" };
    }
  }

  return { valid: true };
}
